import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Snackbar,
  Alert,
  Avatar,
  Tooltip,
  Menu,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Divider,
  Stack,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DeleteOutline,
  PersonAdd,
  EditOutlined,
  Add as AddIcon,
  SettingsOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import CleaningScheduleService from '../../services/cleaningSchedule.service';
import CleaningScheduleAvailabilityDialog from './CleaningScheduleAvailabilityDialog';
import ClientDataService from '../../services/client.service';
import EmployeeService from '../../services/employee.service';
import CleaningDetailService from '../../services/cleaningDetail.service';
import RecurrenceService from '../../services/recurrence.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const GRID_START_HOUR = 6;
const GRID_END_HOUR = 20;
const HOUR_PX = 72;
const GRID_HEIGHT = (GRID_END_HOUR - GRID_START_HOUR) * HOUR_PX;

/** True when creating a calendar assignment: confirm this date vs whole series (one-off shows a single confirm). */
function needsAssignmentScopePrompt(meta) {
  return Boolean(meta?.cleaning_detail_id);
}

function startOfMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseHm(t) {
  if (!t) return GRID_START_HOUR * 60;
  const [h, m] = t.split(':').map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function formatHmFromMinutes(totalMin) {
  let m = Math.round(Math.max(0, totalMin));
  const day = 24 * 60;
  if (m >= day) m %= day;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Job length in hours: prefer API `horas`, else span of start–end. */
function eventTotalHours(ev) {
  if (!ev) return NaN;
  const fromField = Number(ev.horas);
  if (Number.isFinite(fromField) && fromField >= 0) return fromField;
  const start = parseHm(ev.start_time);
  const end = ev.end_time ? parseHm(ev.end_time) : NaN;
  if (!Number.isFinite(end)) return NaN;
  return Math.max(0, (end - start) / 60);
}

function formatEventTotalHours(ev) {
  const h = eventTotalHours(ev);
  if (!Number.isFinite(h)) return null;
  return String(Math.round(h * 100) / 100);
}

/** How many cleaners are represented on this scheduled card (merged or single). */
function scheduledCleanerCount(ev) {
  if (!ev || ev.type !== 'scheduled') return 1;
  const extras = Array.isArray(ev.extra_employees) ? ev.extra_employees.length : 0;
  return 1 + extras;
}

/** Split total job hours across cleaners on the same shift (same calendar block). */
function perPersonJobHours(ev) {
  const total = eventTotalHours(ev);
  const n = scheduledCleanerCount(ev);
  if (!Number.isFinite(total) || n < 1) return null;
  return Math.round((total / n) * 100) / 100;
}

function formatPerPersonJobHours(ev) {
  const v = perPersonJobHours(ev);
  if (v == null || !Number.isFinite(v)) return null;
  return String(v);
}

/** Calendar height & lane overlap: each cleaner’s share of the job duration from job start. */
function effectiveSlotHours(ev) {
  const total = eventTotalHours(ev);
  const base = Number.isFinite(total) && total >= 0 ? total : Number(ev?.horas);
  if (!Number.isFinite(base) || base < 0) return Number(ev?.horas) || 0;
  if (ev?.type !== 'scheduled') return base;
  const n = scheduledCleanerCount(ev);
  if (n <= 1) return base;
  return base / n;
}

/** What to print on the card / dialog: each person’s segment vs full booking end. */
function displayTimeRange(ev) {
  const st = String(ev?.start_time || '').slice(0, 5);
  const jobEnd = ev?.end_time ? String(ev.end_time).slice(0, 5) : '';
  if (ev?.type !== 'scheduled' || scheduledCleanerCount(ev) <= 1) {
    return {
      primaryStart: st,
      primaryEnd: jobEnd || st,
      jobEnd: jobEnd || null,
      showJobFootnote: false,
    };
  }
  const startMin = parseHm(ev.start_time);
  const h = effectiveSlotHours(ev);
  const endMin = startMin + h * 60;
  const primaryEnd = formatHmFromMinutes(endMin);
  return {
    primaryStart: st,
    primaryEnd,
    jobEnd: jobEnd || null,
    showJobFootnote: Boolean(jobEnd && jobEnd !== primaryEnd),
  };
}

function mergeScheduledTracksForDisplay(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  const buckets = new Map();

  const keyFor = (ev) => {
    if (!ev || ev.type !== 'scheduled' || !ev.cleaning_detail_id) return null;
    const st = String(ev.start_time || '').slice(0, 5);
    return `${ev.cleaning_detail_id}|${ev.service_date}|${st}`;
  };

  const out = [];
  for (const ev of arr) {
    const k = keyFor(ev);
    if (!k) {
      out.push(ev);
      continue;
    }
    if (!buckets.has(k)) {
      buckets.set(k, { ...ev, extra_employees: [], needs_attention: Boolean(ev.needs_attention) });
      out.push(buckets.get(k));
      continue;
    }

    const base = buckets.get(k);
    base.needs_attention = Boolean(base.needs_attention || ev.needs_attention);
    const emp = ev.employee;
    if (emp?.id && String(emp.id) !== String(base.employee?.id)) {
      base.extra_employees = base.extra_employees || [];
      const exists = base.extra_employees.some((x) => String(x.id) === String(emp.id));
      if (!exists) {
        base.extra_employees.push({
          id: emp.id,
          name: emp.name,
          color: emp.color,
          track_id: ev.id,
        });
      }
    }
  }

  return out;
}

function computeLanesForDay(list) {
  const items = (list || []).map((ev) => {
    const start = parseHm(ev.start_time);
    const end = start + effectiveSlotHours(ev) * 60;
    return { ev, start, end };
  });
  items.sort((a, b) => a.start - b.start || a.end - b.end);

  const laneEnds = [];
  const placed = [];
  for (const item of items) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.start) lane++;
    if (lane === laneEnds.length) laneEnds.push(item.end);
    else laneEnds[lane] = item.end;
    placed.push({ ...item, lane });
  }

  const laneCount = Math.max(1, laneEnds.length);
  return placed.map((p) => ({ ...p.ev, _lane: p.lane, _laneCount: laneCount }));
}

/** All tracks on a merged scheduled card (for dialog remove). */
function shiftTrackRows(ev) {
  if (!ev || ev.type !== 'scheduled') return [];
  const rows = [];
  if (ev.id != null && ev.employee) {
    rows.push({ trackId: ev.id, employee: ev.employee });
  }
  (ev.extra_employees || []).forEach((ex) => {
    if (ex.track_id != null && ex.id != null) {
      rows.push({
        trackId: ex.track_id,
        employee: { id: ex.id, name: ex.name, color: ex.color },
      });
    }
  });
  return rows;
}

function formatScheduleLongDate(iso, locale) {
  if (!iso) return '';
  const parts = String(iso).split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return String(iso);
  const dt = new Date(parts[0], parts[1] - 1, parts[2]);
  return dt.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function lighten(hex, amount = 0.35) {
  if (!hex || !hex.startsWith('#')) return '#8b5cf6';
  const h = hex.slice(1);
  const n = parseInt(h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h, 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  r = Math.round(r + (255 - r) * amount);
  g = Math.round(g + (255 - g) * amount);
  b = Math.round(b + (255 - b) * amount);
  return `rgb(${r},${g},${b})`;
}

const CleaningScheduleDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('calendar.edit');
  const canCreateJob = hasPermission('cleaning-details.create');
  const canEditCleaningJob = hasPermission('cleaning-details.edit');

  const [weekAnchor, setWeekAnchor] = useState(() => startOfMonday(new Date()));
  const [clientId, setClientId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [assignJob, setAssignJob] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSelectedIds, setAssignSelectedIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignScopeOpen, setAssignScopeOpen] = useState(false);
  const [assignScopeMode, setAssignScopeMode] = useState(null);
  const [pendingProjectedEv, setPendingProjectedEv] = useState(null);
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardDialogEv, setCardDialogEv] = useState(null);
  const [shiftDeletingId, setShiftDeletingId] = useState(null);
  const [cardDialogBusy, setCardDialogBusy] = useState(false);
  const [createAnchor, setCreateAnchor] = useState(null);
  const [onDemandOpen, setOnDemandOpen] = useState(false);
  const [savingOnDemand, setSavingOnDemand] = useState(false);
  const [onDemandForm, setOnDemandForm] = useState({
    client_id: '',
    service_date: '',
    start_time: '09:00',
    horas: '2',
    hourly_rate: '',
    wages: '0',
    notes: '',
  });
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [recurringConflictOpen, setRecurringConflictOpen] = useState(false);
  const [recurringConflictRows, setRecurringConflictRows] = useState([]);
  const [recurringConflictPreviewLoading, setRecurringConflictPreviewLoading] = useState(false);

  const weekStartStr = useMemo(() => toYmd(weekAnchor), [weekAnchor]);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const params = { week_start: weekStartStr };
      if (clientId) params.client_id = clientId;
      if (employeeId) params.employee_id = employeeId;
      const res = await CleaningScheduleService.getWeek(params);
      setPayload(res.data);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningSchedule.loadError'),
        severity: 'error',
      });
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [weekStartStr, clientId, employeeId, t]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    const cid = searchParams.get('client_id') || searchParams.get('clientId');
    if (cid) setClientId(String(cid));
  }, [searchParams]);

  useEffect(() => {
    if (!payload?.days?.length) return;
    setSelectedDate((prev) => {
      if (prev && payload.days.some((d) => d.date === prev)) return prev;
      return payload.days[0].date;
    });
  }, [payload]);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          ClientDataService.getClients(),
          EmployeeService.getAll(),
        ]);
        const rawC = cRes.data?.data ?? cRes.data ?? [];
        setClients(Array.isArray(rawC) ? rawC : []);
        const rawE = eRes.data?.data ?? [];
        setEmployees(Array.isArray(rawE) ? rawE : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const selectedDayMeta = useMemo(() => {
    if (!payload?.days || !selectedDate) return null;
    return payload.days.find((d) => d.date === selectedDate) || null;
  }, [payload, selectedDate]);

  const eventsByDate = useMemo(() => {
    const map = {};
    if (!payload) return map;
    (payload.events || []).forEach((ev) => {
      if (!map[ev.service_date]) map[ev.service_date] = [];
      map[ev.service_date].push(ev);
    });
    (payload.unassigned || []).forEach((job) => {
      const st = job.start_time || '09:00';
      const end = job.end_time || st;
      if (!map[job.service_date]) map[job.service_date] = [];
      map[job.service_date].push({
        type: 'unassigned',
        id: null,
        cleaner_id: null,
        service_date: job.service_date,
        start_time: st,
        end_time: end,
        horas: job.horas,
        client: job.client,
        recurrence_label: job.recurrence_label,
        recurrence_one_off: job.recurrence_one_off,
        cleaning_detail_id: job.cleaning_detail_id,
        assignable_cleaners: job.assignable_cleaners || [],
        employee: { name: '', color: '#f59e0b' },
      });
    });
    Object.keys(map).forEach((date) => {
      map[date] = computeLanesForDay(mergeScheduledTracksForDisplay(map[date]));
    });
    return map;
  }, [payload]);

  const unassignedThisWeek = useMemo(() => {
    const list = payload?.unassigned || [];
    return [...list].sort((a, b) => a.service_date.localeCompare(b.service_date));
  }, [payload]);

  const fullDayUnavailableIds = useMemo(() => {
    const s = new Set();
    (payload?.unavailabilities || []).forEach((u) => {
      if (u.unavailable_date === selectedDate && u.is_full_day) {
        s.add(String(u.employee_id));
      }
    });
    return s;
  }, [payload?.unavailabilities, selectedDate]);

  const assignedHoursByCleanerId = useMemo(() => {
    const m = {};
    (payload?.events || []).forEach((ev) => {
      if (ev?.type !== 'scheduled') return;
      if (ev?.service_date !== selectedDate) return;
      const id = ev?.employee?.id ?? ev?.cleaner_id;
      if (id == null) return;
      const key = String(id);
      const h = Number(ev?.horas);
      m[key] = (m[key] || 0) + (Number.isFinite(h) ? h : 0);
    });
    return m;
  }, [payload, selectedDate]);

  /** Sum of **job** hours (one multi-cleaner shift = one job duration, not × cleaners). */
  const totalAssignedHoursForSelectedDay = useMemo(() => {
    const perJob = new Map();
    (payload?.events || []).forEach((ev) => {
      if (ev?.type !== 'scheduled') return;
      if (ev?.service_date !== selectedDate) return;
      const h = eventTotalHours(ev);
      const hours = Number.isFinite(h) && h >= 0 ? h : 0;
      let key;
      if (ev.cleaning_detail_id) {
        const st = String(ev.start_time || '').slice(0, 5);
        key = `job|${ev.cleaning_detail_id}|${ev.service_date}|${st}`;
      } else {
        key = `track|${ev.id ?? `${ev.cleaner_id}-${String(ev.start_time || '')}`}`;
      }
      if (!perJob.has(key)) perJob.set(key, hours);
    });
    let total = 0;
    perJob.forEach((h) => {
      total += h;
    });
    return Math.round(total * 100) / 100;
  }, [payload, selectedDate]);

  const cleanersAssignedList = useMemo(() => {
    if (!selectedDate) return [];
    const all = Array.isArray(employees) ? employees : [];
    const active = all.filter((e) => !e?.status || String(e.status).toLowerCase() === 'active');
    const base = employeeId ? active.filter((e) => String(e.id) === String(employeeId)) : active;

    return base
      .map((e) => ({
        ...e,
        assignedHours: assignedHoursByCleanerId[String(e.id)] || 0,
      }))
      .sort(
        (a, b) =>
          (b.assignedHours - a.assignedHours) || String(a.name || '').localeCompare(String(b.name || ''))
      );
  }, [employees, employeeId, selectedDate, assignedHoursByCleanerId]);

  const weekEndStr = useMemo(() => {
    const parts = weekStartStr.split('-').map((x) => parseInt(x, 10));
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return weekStartStr;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    d.setDate(d.getDate() + 6);
    return toYmd(d);
  }, [weekStartStr]);

  const attentionForSelectedDay = useMemo(() => {
    const list = eventsByDate[selectedDate] || [];
    return list.filter((e) => e.needs_attention && e.type !== 'unassigned');
  }, [eventsByDate, selectedDate]);

  /** Days with unassigned work or scheduled/projected items overlapping time off. */
  const pendingDates = useMemo(() => {
    const s = new Set();
    (payload?.unassigned || []).forEach((job) => {
      if (job?.service_date) s.add(job.service_date);
    });
    (payload?.events || []).forEach((ev) => {
      if (ev?.needs_attention && ev?.service_date) s.add(ev.service_date);
    });
    return s;
  }, [payload]);

  const layoutEvent = (ev) => {
    const startMin = parseHm(ev.start_time);
    const gridStartMin = GRID_START_HOUR * 60;
    const endMin = startMin + effectiveSlotHours(ev) * 60;
    const topMin = Math.max(0, startMin - gridStartMin);
    const bottomMin = Math.min(GRID_END_HOUR * 60 - gridStartMin, endMin - gridStartMin);
    const heightMin = Math.max(24, bottomMin - topMin);
    return {
      top: (topMin / 60) * HOUR_PX,
      height: (heightMin / 60) * HOUR_PX,
    };
  };

  const goPrev = () => {
    const d = new Date(weekAnchor);
    d.setDate(d.getDate() - 7);
    setWeekAnchor(startOfMonday(d));
  };

  const goNext = () => {
    const d = new Date(weekAnchor);
    d.setDate(d.getDate() + 7);
    setWeekAnchor(startOfMonday(d));
  };

  const goToday = () => {
    setWeekAnchor(startOfMonday(new Date()));
  };

  const monthTitle = useMemo(() => {
    const d = new Date(weekAnchor);
    return d.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
  }, [weekAnchor, language]);

  const cardDialogTimeDisplay = useMemo(() => {
    if (!cardDialogEv) return null;
    return displayTimeRange(cardDialogEv);
  }, [cardDialogEv]);

  const openCardDialog = (ev) => {
    if (!ev) return;
    setCardDialogEv(ev);
    setCardDialogOpen(true);
    if (ev.service_date) setSelectedDate(ev.service_date);
  };

  const closeCardDialog = () => {
    if (shiftDeletingId != null || cardDialogBusy) return;
    setCardDialogOpen(false);
    setCardDialogEv(null);
  };

  const removeTrackFromCardDialog = async (trackId) => {
    if (!canEdit || !trackId) return;
    setShiftDeletingId(trackId);
    try {
      await CleaningScheduleService.deleteTrack(trackId);
      setSnackbar({ open: true, message: t('cleaningSchedule.removed'), severity: 'success' });
      setCardDialogOpen(false);
      setCardDialogEv(null);
      loadWeek();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningSchedule.removeError'),
        severity: 'error',
      });
    } finally {
      setShiftDeletingId(null);
    }
  };

  const executePlaceProjectedOnCalendar = async (ev, placement = 'recurring_team') => {
    if (!canEdit || !ev?.cleaning_detail_id || !ev?.service_date || !ev?.employee?.id) return;
    setCardDialogBusy(true);
    try {
      await CleaningScheduleService.createTrack({
        cleaning_detail_id: ev.cleaning_detail_id,
        employee_id: ev.employee.id,
        service_date: ev.service_date,
        placement,
      });
      setSnackbar({ open: true, message: t('cleaningSchedule.assigned'), severity: 'success' });
      setCardDialogOpen(false);
      setCardDialogEv(null);
      loadWeek();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e.message || t('cleaningSchedule.assignError'),
        severity: 'error',
      });
    } finally {
      setCardDialogBusy(false);
    }
  };

  const placeProjectedOnCalendar = async (ev) => {
    if (!canEdit || !ev?.cleaning_detail_id || !ev?.service_date || !ev?.employee?.id) return;
    if (needsAssignmentScopePrompt(ev)) {
      setPendingProjectedEv(ev);
      setAssignScopeMode('projected');
      setAssignScopeOpen(true);
      return;
    }
    await executePlaceProjectedOnCalendar(ev, 'recurring_team');
  };

  const unassignedJobPayloadFromEv = (ev) => ({
    cleaning_detail_id: ev.cleaning_detail_id,
    service_date: ev.service_date,
    start_time: ev.start_time,
    end_time: ev.end_time,
    horas: ev.horas,
    client: ev.client,
    recurrence_label: ev.recurrence_label,
    recurrence_one_off: ev.recurrence_one_off,
    assignable_cleaners: ev.assignable_cleaners || [],
  });

  const getAssignableEmployeesForJobDay = useCallback(
    (cleaningDetailId, serviceDate) => {
      if (!cleaningDetailId || !serviceDate || !payload?.events) return [];
      const assigned = new Set();
      (payload.events || []).forEach((ev) => {
        if (ev.type !== 'scheduled') return;
        if (String(ev.cleaning_detail_id) !== String(cleaningDetailId)) return;
        if (ev.service_date !== serviceDate) return;
        if (ev.employee?.id != null) assigned.add(String(ev.employee.id));
      });
      const all = Array.isArray(employees) ? employees : [];
      const active = all.filter((e) => !e?.status || String(e.status).toLowerCase() === 'active');
      // Important: ignore the calendar "Cleaner" filter when adding/assigning,
      // otherwise the action can become unavailable just because the view is filtered.
      return active
        .filter((e) => !assigned.has(String(e.id)))
        .map((e) => ({
          cleaner_id: null,
          employee: { id: e.id, name: e.name, color: e.color || '#667eea' },
        }));
    },
    [employees, payload]
  );

  const openAssignDialog = (job) => {
    const jd = job?.cleaning_detail_id;
    const sd = job?.service_date;
    const suggestedIds = new Set(
      (job?.assignable_cleaners || [])
        .map((r) => r?.employee?.id)
        .filter((id) => id != null)
        .map((id) => String(id))
    );

    // Allow assigning ANY active employee (not only pre-linked cleaners).
    const rows = jd && sd ? getAssignableEmployeesForJobDay(jd, sd) : job?.assignable_cleaners || [];

    const initial = rows
      .map((r) => r?.employee?.id)
      .filter((id) => id != null)
      .map((id) => String(id))
      .filter((id) => suggestedIds.has(id));

    setAssignJob({ ...job, assignable_cleaners: rows, assignAddMode: false });
    setAssignSelectedIds(Array.from(new Set(initial)));
    setAssignOpen(true);
  };

  const openAddCleanerFromScheduledEvent = (ev) => {
    if (!canEdit || !ev?.cleaning_detail_id || !ev?.service_date) return;
    const rows = getAssignableEmployeesForJobDay(ev.cleaning_detail_id, ev.service_date);
    if (!rows.length) {
      setSnackbar({
        open: true,
        message: t('cleaningSchedule.assignAddNoOneAvailable'),
        severity: 'info',
      });
      return;
    }
    setAssignJob({
      cleaning_detail_id: ev.cleaning_detail_id,
      service_date: ev.service_date,
      start_time: ev.start_time,
      end_time: ev.end_time,
      client: ev.client,
      recurrence_label: ev.recurrence_label,
      recurrence_one_off: ev.recurrence_one_off,
      assignable_cleaners: rows,
      assignAddMode: true,
    });
    setAssignSelectedIds([]);
    setAssignOpen(true);
  };

  /** Projected card: other cleaners only (linked employee uses “Place on calendar”). */
  const openAddCleanerFromProjectedEvent = (ev) => {
    if (!canEdit || !ev?.cleaning_detail_id || !ev?.service_date) return;
    let rows = getAssignableEmployeesForJobDay(ev.cleaning_detail_id, ev.service_date);
    const linkedId = ev.employee?.id;
    if (linkedId != null) {
      rows = rows.filter((r) => String(r.employee?.id) !== String(linkedId));
    }
    if (!rows.length) {
      setSnackbar({
        open: true,
        message: t('cleaningSchedule.assignAddNoOneAvailable'),
        severity: 'info',
      });
      return;
    }
    setAssignJob({
      cleaning_detail_id: ev.cleaning_detail_id,
      service_date: ev.service_date,
      start_time: ev.start_time,
      end_time: ev.end_time,
      client: ev.client,
      recurrence_label: ev.recurrence_label,
      recurrence_one_off: ev.recurrence_one_off,
      assignable_cleaners: rows,
      assignAddMode: true,
    });
    setAssignSelectedIds([]);
    setAssignOpen(true);
  };

  const toggleAssignEmployee = (empId) => {
    const idStr = String(empId);
    setAssignSelectedIds((prev) => (prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]));
  };

  const saveMultiAssign = async (placement = 'recurring_team') => {
    if (!canEdit || !assignJob) return;
    const serviceDate = assignJob.service_date;
    const cleaningDetailId = assignJob.cleaning_detail_id;
    if (!serviceDate || !cleaningDetailId) return;
    if (!assignSelectedIds.length) {
      setSnackbar({ open: true, message: t('cleaningSchedule.assignPickOne'), severity: 'error' });
      return;
    }

    setAssignSaving(true);
    try {
      const results = await Promise.allSettled(
        assignSelectedIds.map((id) =>
          CleaningScheduleService.createTrack({
            cleaning_detail_id: cleaningDetailId,
            employee_id: parseInt(id, 10),
            service_date: serviceDate,
            placement,
          })
        )
      );

      const rejected = results.filter((r) => r.status === 'rejected');
      if (rejected.length === results.length) {
        const msg = rejected[0]?.reason?.response?.data?.message;
        throw new Error(msg || t('cleaningSchedule.assignError'));
      }

      setSnackbar({
        open: true,
        message:
          rejected.length > 0
            ? t('cleaningSchedule.assignedPartial', { ok: results.length - rejected.length, fail: rejected.length })
            : t('cleaningSchedule.assigned'),
        severity: rejected.length > 0 ? 'warning' : 'success',
      });
      setAssignOpen(false);
      setAssignJob(null);
      setAssignSelectedIds([]);
      loadWeek();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e.message || t('cleaningSchedule.assignError'),
        severity: 'error',
      });
    } finally {
      setAssignSaving(false);
    }
  };

  const closeAssignScope = () => {
    if (assignSaving || cardDialogBusy) return;
    setAssignScopeOpen(false);
    setAssignScopeMode(null);
    setPendingProjectedEv(null);
    setRecurringConflictOpen(false);
    setRecurringConflictRows([]);
  };

  const confirmAssignScope = (placement) => {
    setAssignScopeOpen(false);
    setRecurringConflictOpen(false);
    setRecurringConflictRows([]);
    const mode = assignScopeMode;
    const projected = pendingProjectedEv;
    setAssignScopeMode(null);
    setPendingProjectedEv(null);
    if (mode === 'multiAssign') {
      saveMultiAssign(placement);
    } else if (mode === 'projected' && projected) {
      executePlaceProjectedOnCalendar(projected, placement);
    }
  };

  const closeRecurringConflictDialog = () => {
    setRecurringConflictOpen(false);
    setRecurringConflictRows([]);
  };

  const confirmRecurringDespiteConflicts = () => {
    closeRecurringConflictDialog();
    confirmAssignScope('recurring_team');
  };

  const handleWholeSeriesChosen = async () => {
    const placement = 'recurring_team';
    const cleaningDetailId =
      assignScopeMode === 'projected' ? pendingProjectedEv?.cleaning_detail_id : assignJob?.cleaning_detail_id;
    const serviceDate =
      assignScopeMode === 'projected' ? pendingProjectedEv?.service_date : assignJob?.service_date;
    let employeeIds = [];
    if (assignScopeMode === 'projected') {
      if (pendingProjectedEv?.employee?.id != null) employeeIds = [pendingProjectedEv.employee.id];
    } else {
      employeeIds = assignSelectedIds.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));
    }
    if (!cleaningDetailId || !serviceDate || !employeeIds.length) {
      confirmAssignScope(placement);
      return;
    }
    setRecurringConflictPreviewLoading(true);
    try {
      const res = await CleaningScheduleService.previewRecurringConflicts({
        cleaning_detail_id: cleaningDetailId,
        employee_ids: employeeIds,
        service_date: serviceDate,
        placement,
      });
      const list = Array.isArray(res.data?.conflicts) ? res.data.conflicts : [];
      if (list.length > 0) {
        setRecurringConflictRows(list);
        setRecurringConflictOpen(true);
      } else {
        confirmAssignScope(placement);
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningSchedule.recurringConflictPreviewError'),
        severity: 'error',
      });
    } finally {
      setRecurringConflictPreviewLoading(false);
    }
  };

  const openCreateMenu = (e) => {
    setCreateAnchor(e.currentTarget);
  };

  const closeCreateMenu = () => {
    setCreateAnchor(null);
  };

  const openOnDemandDialog = (opts = {}) => {
    const date = opts.date || selectedDate || weekStartStr;
    setOnDemandForm((f) => ({
      ...f,
      client_id: opts.newClient ? '' : (clientId || f.client_id || ''),
      service_date: date || '',
      start_time: '09:00',
      horas: f.horas || '2',
      hourly_rate: f.hourly_rate || '',
      wages: f.wages || '0',
      notes: '',
    }));
    setOnDemandOpen(true);
  };

  const handleOnDemandChange = (e) => {
    const { name, value } = e.target;
    setOnDemandForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveOnDemand = async () => {
    if (!onDemandForm.client_id || !onDemandForm.service_date || !onDemandForm.horas || !onDemandForm.hourly_rate) {
      setSnackbar({ open: true, message: t('cleaningSchedule.onDemandFillRequired'), severity: 'error' });
      return;
    }
    setSavingOnDemand(true);
    try {
      // Schedule page currently only renders jobs WITH recurrence_id.
      // To create a true one-off while still appearing on the calendar,
      // we create a 1-day recurrence (start_date == end_date == service_date).
      const recRes = await RecurrenceService.create({
        schedule: 'daily',
        start_date: onDemandForm.service_date,
        end_date: onDemandForm.service_date,
        start_time: onDemandForm.start_time,
        every_days: 1,
      });
      const createdRec = recRes.data?.data;
      const recurrenceId =
        createdRec && typeof createdRec === 'object'
          ? createdRec.id ?? createdRec.data?.id
          : null;

      if (!recurrenceId) {
        throw new Error('Missing recurrence id');
      }

      const horas = parseFloat(onDemandForm.horas);
      const rate = parseFloat(onDemandForm.hourly_rate);
      const income = Number.isFinite(horas) && Number.isFinite(rate) ? Math.round(horas * rate * 100) / 100 : 0;

      await CleaningDetailService.create({
        client_id: parseInt(onDemandForm.client_id, 10),
        recurrence_id: recurrenceId,
        notes: onDemandForm.notes || null,
        horas: horas,
        income,
        wages: parseFloat(onDemandForm.wages) || 0,
        hourly_rate: rate,
      });

      setOnDemandOpen(false);
      setSnackbar({ open: true, message: t('cleaningSchedule.onDemandCreated'), severity: 'success' });
      loadWeek();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningSchedule.onDemandCreateError'),
        severity: 'error',
      });
    } finally {
      setSavingOnDemand(false);
    }
  };

  const hourLabels = [];
  for (let h = GRID_START_HOUR; h < GRID_END_HOUR; h++) {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h > 12 ? h - 12 : h === 0 ? 12 : h;
    hourLabels.push(
      <Box
        key={h}
        sx={{
          height: HOUR_PX,
          pr: 1,
          textAlign: 'right',
          pt: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: 11,
          color: 'text.secondary',
        }}
      >
        {hr}:00 {ampm}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 100px)',
        minHeight: 560,
        bgcolor: '#f1f5f9',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 2,
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              {t('cleaningSchedule.title')}
            </Typography>
            <Chip label={t('cleaningSchedule.week')} color="primary" variant="outlined" size="small" />
            {typeof payload?.attention_count === 'number' && payload.attention_count > 0 && (
              <Chip
                icon={<WarningAmberOutlined sx={{ '&&': { fontSize: 18 } }} />}
                label={t('cleaningSchedule.attentionWeekCount', { count: payload.attention_count })}
                color="warning"
                variant="filled"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
            <Tooltip title={t('cleaningSchedule.availabilityDialogTooltip')}>
              <IconButton
                size="small"
                onClick={() => setAvailabilityDialogOpen(true)}
                sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
                aria-label={t('cleaningSchedule.availabilityDialogTitle')}
              >
                <SettingsOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('cleaningSchedule.filterClient')}</InputLabel>
              <Select
                label={t('cleaningSchedule.filterClient')}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                <MenuItem value="">{t('cleaningSchedule.all')}</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('cleaningSchedule.filterCleaner')}</InputLabel>
              <Select
                label={t('cleaningSchedule.filterCleaner')}
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <MenuItem value="">{t('cleaningSchedule.all')}</MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canCreateJob && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openCreateMenu}
                sx={{ ml: { xs: 0, sm: 1 }, fontWeight: 700 }}
              >
                {t('cleaningSchedule.add')}
              </Button>
            )}
          </Box>
        </Paper>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={goPrev} size="small" sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {monthTitle}
              </Typography>
              <IconButton onClick={goNext} size="small" sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <ChevronRight />
              </IconButton>
            </Box>
            <Button onClick={goToday} color="primary" sx={{ fontWeight: 600 }}>
              {t('cleaningSchedule.today')}
            </Button>
          </Box>

          {loading && !payload ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '24px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '88px repeat(7, minmax(0, 1fr))',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Box sx={{ p: 1.5, borderRight: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t('cleaningSchedule.timeTeam')}
                  </Typography>
                </Box>
                {payload?.days?.map((d) => {
                  const active = selectedDate === d.date;
                  const hasPending = pendingDates.has(d.date);
                  const barColor =
                    d.capacity_level === 'full'
                      ? '#f43f5e'
                      : d.capacity_level === 'warning'
                        ? '#f59e0b'
                        : '#10b981';
                  return (
                    <Box
                      key={d.date}
                      onClick={() => setSelectedDate(d.date)}
                      sx={{
                        p: 1.5,
                        textAlign: 'center',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        bgcolor: active ? 'primary.50' : 'transparent',
                        '&:last-of-type': { borderRight: 'none' },
                      }}
                    >
                      <Typography variant="caption" color={active ? 'primary' : 'text.secondary'} fontWeight={600}>
                        {d.weekday_short}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.75,
                          minHeight: 40,
                        }}
                      >
                        <Typography variant="h5" fontWeight={700} color={active ? 'primary.main' : 'text.primary'}>
                          {d.day}
                        </Typography>
                        {hasPending && (
                          <Tooltip title={t('cleaningSchedule.dayPendingTooltip')}>
                            <Box
                              component="span"
                              aria-label={t('cleaningSchedule.dayPendingAria')}
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'warning.main',
                                border: '2px solid',
                                borderColor: active ? 'primary.50' : 'grey.50',
                                flexShrink: 0,
                                boxSizing: 'border-box',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, d.capacity_percent)}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor: barColor,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', position: 'relative', minHeight: GRID_HEIGHT }}>
                <Box
                  sx={{
                    width: 88,
                    flexShrink: 0,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    zIndex: 2,
                  }}
                >
                  {hourLabels}
                </Box>
                <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', position: 'relative' }}>
                  {payload?.days?.map((d) => (
                    <Box
                      key={d.date}
                      sx={{
                        position: 'relative',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        height: GRID_HEIGHT,
                        backgroundImage: `repeating-linear-gradient(
                          to bottom,
                          transparent,
                          transparent ${HOUR_PX - 1}px,
                          rgba(0,0,0,0.06) ${HOUR_PX - 1}px,
                          rgba(0,0,0,0.06) ${HOUR_PX}px
                        )`,
                        bgcolor:
                          d.capacity_level === 'full'
                            ? 'rgba(244,63,94,0.04)'
                            : d.capacity_level === 'warning'
                              ? 'rgba(245,158,11,0.06)'
                              : 'transparent',
                        '&:last-of-type': { borderRight: 'none' },
                      }}
                    >
                      {(eventsByDate[d.date] || []).map((ev) => {
                        const { top, height } = layoutEvent(ev);
                        const isUnassigned = ev.type === 'unassigned';
                        const isProjected = ev.type === 'projected';
                        const addableCleanersForCard =
                          ev.type === 'scheduled'
                            ? getAssignableEmployeesForJobDay(ev.cleaning_detail_id, ev.service_date)
                            : [];
                        const timeDisplay = displayTimeRange(ev);
                        const col = isUnassigned ? '#f59e0b' : ev.employee?.color || '#7c3aed';
                        const inset = 4;
                        const laneGap = 6;
                        const laneCount = Math.max(1, ev._laneCount || 1);
                        const lane = Math.max(0, ev._lane || 0);
                        const laneWidth = `calc((100% - ${inset * 2}px - ${(laneCount - 1) * laneGap}px) / ${laneCount})`;
                        const laneLeft = `calc(${inset}px + (${laneWidth} + ${laneGap}px) * ${lane})`;
                        return (
                          <Tooltip
                            key={
                              isUnassigned
                                ? `unassigned-${ev.cleaning_detail_id}-${ev.service_date}`
                                : ev.type === 'scheduled' && ev.cleaning_detail_id
                                  ? `scheduled-slot-${ev.cleaning_detail_id}-${ev.service_date}-${String(ev.start_time || '').slice(0, 5)}`
                                  : `${ev.type}-${ev.cleaner_id}-${ev.service_date}-${ev.start_time}`
                            }
                            title={
                              isUnassigned
                                ? `${ev.client?.name} · ${t('cleaningSchedule.unassigned')} · ${ev.start_time}–${ev.end_time}`
                                : ev.type === 'scheduled' && scheduledCleanerCount(ev) > 1 && formatPerPersonJobHours(ev)
                                  ? t('cleaningSchedule.tooltipScheduledMulti', {
                                      client: ev.client?.name || '',
                                      eachStart: timeDisplay.primaryStart,
                                      eachEnd: timeDisplay.primaryEnd,
                                      jobEnd: timeDisplay.jobEnd || '—',
                                      each: formatPerPersonJobHours(ev),
                                      count: scheduledCleanerCount(ev),
                                    })
                                  : `${ev.client?.name} · ${timeDisplay.primaryStart}–${timeDisplay.primaryEnd}${
                                    ev.needs_attention ? ` · ${t('cleaningSchedule.attentionTooltip')}` : ''
                                  }`
                            }
                          >
                            <Box
                              onClick={() => openCardDialog(ev)}
                              sx={{
                                position: 'absolute',
                                left: laneLeft,
                                width: laneWidth,
                                top,
                                height,
                                borderRadius: 2,
                                px: 1,
                                py: 0.5,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                background: isUnassigned
                                  ? 'linear-gradient(135deg, rgba(245,158,11,0.35), rgba(251,191,36,0.45))'
                                  : isProjected
                                    ? `linear-gradient(135deg, ${lighten(col, 0.5)}, ${lighten(col, 0.65)})`
                                    : `linear-gradient(135deg, ${col}, ${lighten(col, 0.15)})`,
                                color: isUnassigned ? '#78350f' : '#fff',
                                border: isProjected || isUnassigned ? '2px dashed' : 'none',
                                borderColor: isUnassigned ? 'warning.main' : 'primary.main',
                                boxShadow:
                                  isProjected || isUnassigned
                                    ? 'none'
                                    : ev.needs_attention
                                      ? '0 0 0 3px rgba(245, 158, 11, 0.95), 0 4px 14px rgba(124,58,237,0.25)'
                                      : '0 4px 14px rgba(124,58,237,0.35)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                zIndex: 3,
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={isUnassigned ? t('cleaningSchedule.unassigned') : ev.recurrence_label}
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    bgcolor: isUnassigned ? 'rgba(120,53,15,0.15)' : 'rgba(255,255,255,0.25)',
                                    color: 'inherit',
                                  }}
                                />
                                {canEdit && ev.type === 'scheduled' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <Tooltip title={t('cleaningSchedule.assignAddCleanerTooltip')}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          sx={{ color: 'inherit', p: 0.25 }}
                                          disabled={addableCleanersForCard.length === 0}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAddCleanerFromScheduledEvent(ev);
                                          }}
                                        >
                                          <PersonAdd sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>
                              <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {ev.client?.name}
                              </Typography>
                              {ev.type === 'scheduled' && ev.employee?.name && !(Array.isArray(ev.extra_employees) && ev.extra_employees.length) && (
                                <Typography variant="caption" sx={{ opacity: 0.92, fontSize: 9, lineHeight: 1.2, display: 'block' }} noWrap>
                                  {ev.employee.name}
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: 10 }}>
                                {timeDisplay.primaryStart} – {timeDisplay.primaryEnd}
                              </Typography>
                              {timeDisplay.showJobFootnote && timeDisplay.jobEnd && (
                                <Typography variant="caption" sx={{ opacity: 0.82, fontSize: 8.5, lineHeight: 1.2, display: 'block' }}>
                                  {t('cleaningSchedule.fullJobWindowNote', { end: timeDisplay.jobEnd })}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {isUnassigned ? (
                                  <Avatar
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      fontSize: 10,
                                      border: '1px solid rgba(120,53,15,0.4)',
                                      bgcolor: 'warning.light',
                                    }}
                                  >
                                    ?
                                  </Avatar>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        fontSize: 10,
                                        border: '1px solid rgba(255,255,255,0.5)',
                                        bgcolor: ev.employee?.color || undefined,
                                      }}
                                    >
                                      {ev.employee?.name?.charAt(0) || '?'}
                                    </Avatar>
                                    {Array.isArray(ev.extra_employees) &&
                                      ev.extra_employees.slice(0, 3).map((ex) => (
                                        <Avatar
                                          key={ex.track_id ?? ex.id}
                                          sx={{
                                            width: 20,
                                            height: 20,
                                            fontSize: 10,
                                            ml: -0.75,
                                            border: '1px solid rgba(255,255,255,0.55)',
                                            bgcolor: ex.color || undefined,
                                          }}
                                        >
                                          {ex.name?.charAt(0) || '?'}
                                        </Avatar>
                                      ))}
                                    {Array.isArray(ev.extra_employees) && ev.extra_employees.length > 3 && (
                                      <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.9, fontSize: 10 }}>
                                        +{ev.extra_employees.length - 3}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: 320,
          flexShrink: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          borderRadius: 0,
          overflow: 'auto',
        }}
      >
        {selectedDayMeta && (
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography fontWeight={700}>
                {new Date(selectedDayMeta.date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('cleaningSchedule.dailyCapacity')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('cleaningSchedule.dailyCapacityHint')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedDayMeta.capacity_percent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, selectedDayMeta.capacity_percent)}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 1,
                '& .MuiLinearProgress-bar': {
                  bgcolor:
                    selectedDayMeta.capacity_level === 'full'
                      ? '#f43f5e'
                      : selectedDayMeta.capacity_level === 'warning'
                        ? '#f59e0b'
                        : '#10b981',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t('cleaningSchedule.totalAssignedHoursLabel', { hours: String(totalAssignedHoursForSelectedDay || 0) })}
            </Typography>
            {selectedDayMeta.capacity_level === 'warning' && (
              <Typography variant="caption" color="warning.main">
                {t('cleaningSchedule.capacityWarning')}
              </Typography>
            )}
            {selectedDayMeta.capacity_level === 'full' && (
              <Typography variant="caption" color="error">
                {t('cleaningSchedule.capacityFull')}
              </Typography>
            )}
          </Box>
        )}

        {attentionForSelectedDay.length > 0 && (
          <Box
            sx={{
              bgcolor: 'rgba(245, 158, 11, 0.12)',
              borderRadius: 3,
              p: 2,
              border: '1px solid',
              borderColor: 'warning.light',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <WarningAmberOutlined color="warning" fontSize="small" />
              <Typography fontWeight={700}>{t('cleaningSchedule.attentionDayTitle')}</Typography>
              <Chip size="small" label={attentionForSelectedDay.length} color="warning" />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('cleaningSchedule.attentionDayHint')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {attentionForSelectedDay.map((ev) => (
                <Paper
                  key={`att-${ev.type}-${ev.id ?? ev.cleaner_id}-${ev.service_date}-${ev.start_time}`}
                  variant="outlined"
                  sx={{ p: 1, borderColor: 'warning.main', cursor: 'pointer' }}
                  onClick={() => openCardDialog(ev)}
                >
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {ev.client?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ev.start_time}–{ev.end_time}
                    {ev.employee?.name ? ` · ${ev.employee.name}` : ''}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ bgcolor: 'white', borderRadius: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography fontWeight={700}>{t('cleaningSchedule.availableCleaners')}</Typography>
            <Chip size="small" label={cleanersAssignedList.length} />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('cleaningSchedule.cleanersHoursHint')}
          </Typography>
          {cleanersAssignedList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('cleaningSchedule.noneActiveCleaners')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 160, overflow: 'auto', pr: 1 }}>
              {cleanersAssignedList.map((e) => {
                const isOff = fullDayUnavailableIds.has(String(e.id));
                const hours = Number(e.assignedHours) || 0;
                const hoursStr = String(Math.round(hours * 100) / 100);
                return (
                  <Box
                    key={e.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      opacity: isOff ? 0.6 : 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, bgcolor: e.color || 'primary.main' }}>
                      {e.name?.charAt(0) || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                        {e.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {t('cleaningSchedule.assignedHoursLabel', { hours: hoursStr })}
                      </Typography>
                      {isOff && (
                        <Typography variant="caption" color="error.main" display="block" noWrap>
                          {t('cleaningSchedule.offFullDayLabel')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography fontWeight={700}>{t('cleaningSchedule.unassigned')}</Typography>
            <Chip size="small" label={unassignedThisWeek.length} />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('cleaningSchedule.unassignedWeekHint')}
          </Typography>
          {unassignedThisWeek.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('cleaningSchedule.noneUnassigned')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {unassignedThisWeek.map((job) => (
                <Paper
                  key={`${job.cleaning_detail_id}-${job.service_date}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip size="small" label={job.recurrence_label} color="primary" variant="outlined" />
                    {canEdit && (job.assignable_cleaners || []).length > 0 && (
                      <IconButton size="small" onClick={() => openAssignDialog(job)}>
                        <PersonAdd fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography fontWeight={600} variant="body2" gutterBottom>
                    {job.client?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {job.service_date} · {job.horas}h ·{' '}
                    {(job.assignable_cleaners || []).length || job.cleaners_required || 1}{' '}
                    {t('cleaningSchedule.slots')}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Auto-schedule remaining button removed as requested */}
      </Paper>

      <Dialog open={assignOpen} onClose={() => !assignSaving && setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {assignJob?.assignAddMode ? t('cleaningSchedule.assignAddCleanerTitle') : t('cleaningSchedule.assignMultipleTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {assignJob?.assignAddMode ? t('cleaningSchedule.assignAddCleanerHint') : t('cleaningSchedule.assignMultipleHint')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(assignJob?.assignable_cleaners || []).map((row) => {
              const empId = row?.employee?.id;
              if (!empId) return null;
              const checked = assignSelectedIds.includes(String(empId));
              return (
                <Box
                  key={row.cleaner_id ?? `emp-${empId}`}
                  onClick={() => toggleAssignEmployee(empId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: checked ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                    bgcolor: checked ? 'primary.50' : 'transparent',
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: row.employee.color }}>{row.employee.name?.charAt(0)}</Avatar>
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                    {row.employee.name}
                  </Typography>
                  <Chip size="small" label={checked ? t('cleaningSchedule.selected') : t('cleaningSchedule.tapToSelect')} />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignSaving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!assignSelectedIds.length) {
                setSnackbar({ open: true, message: t('cleaningSchedule.assignPickOne'), severity: 'error' });
                return;
              }
              if (needsAssignmentScopePrompt(assignJob)) {
                setAssignScopeMode('multiAssign');
                setAssignScopeOpen(true);
                return;
              }
              saveMultiAssign('recurring_team');
            }}
            disabled={assignSaving}
          >
            {assignSaving ? t('common.saving') : t('cleaningSchedule.assignSave')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignScopeOpen}
        onClose={() => {
          if (assignSaving || cardDialogBusy) return;
          closeAssignScope();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cleaningSchedule.assignScopeTitle')}</DialogTitle>
        <DialogContent>
          {(() => {
            const src = assignScopeMode === 'projected' ? pendingProjectedEv : assignJob;
            const oneOff = Boolean(src?.recurrence_one_off);
            return (
              <Typography variant="body2" color="text.secondary">
                {oneOff ? t('cleaningSchedule.assignScopeDescriptionOneOff') : t('cleaningSchedule.assignScopeDescription')}
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={closeAssignScope} disabled={assignSaving || cardDialogBusy || recurringConflictPreviewLoading}>
            {t('common.cancel')}
          </Button>
          {(() => {
            const src = assignScopeMode === 'projected' ? pendingProjectedEv : assignJob;
            const showSeries = Boolean(src && !src.recurrence_one_off);
            if (!showSeries) {
              return (
                <Button variant="contained" onClick={() => confirmAssignScope('single_day')} disabled={assignSaving || cardDialogBusy}>
                  {t('cleaningSchedule.assignScopeConfirmOneOff')}
                </Button>
              );
            }
            return (
              <>
                <Button variant="outlined" onClick={() => confirmAssignScope('single_day')} disabled={assignSaving || cardDialogBusy}>
                  {t('cleaningSchedule.assignScopeThisVisit')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleWholeSeriesChosen()}
                  disabled={assignSaving || cardDialogBusy || recurringConflictPreviewLoading}
                >
                  {recurringConflictPreviewLoading ? t('common.loading') : t('cleaningSchedule.assignScopeWholeSeries')}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>

      <Dialog open={recurringConflictOpen} onClose={closeRecurringConflictDialog} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>{t('cleaningSchedule.recurringConflictTitle')}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('cleaningSchedule.recurringConflictIntro')}
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflow: 'auto' }}>
            {recurringConflictRows.map((row, idx) => (
              <Paper key={`${row.date}-${row.other_cleaning_detail_id}-${row.employee_id}-${idx}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {row.date} · {row.employee_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('cleaningSchedule.recurringConflictThisJobWindow', {
                    start: row.this_start,
                    end: row.this_end,
                  })}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {t('cleaningSchedule.recurringConflictOtherJob', {
                    client: row.other_client_name || '—',
                    start: row.other_start,
                    end: row.other_end,
                  })}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={closeRecurringConflictDialog}>{t('cleaningSchedule.recurringConflictGoBack')}</Button>
          <Button variant="contained" color="warning" onClick={confirmRecurringDespiteConflicts}>
            {t('cleaningSchedule.recurringConflictAssignAnyway')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardDialogOpen} onClose={closeCardDialog} maxWidth="sm" fullWidth>
        {cardDialogEv && (
          <>
            <DialogTitle sx={{ pr: 6 }}>{cardDialogEv.client?.name || '—'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatScheduleLongDate(cardDialogEv.service_date, language)}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    {cardDialogTimeDisplay
                      ? `${cardDialogTimeDisplay.primaryStart} – ${cardDialogTimeDisplay.primaryEnd}`
                      : `${cardDialogEv.start_time} – ${cardDialogEv.end_time}`}
                  </Typography>
                  {cardDialogTimeDisplay?.showJobFootnote && cardDialogTimeDisplay.jobEnd && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('cleaningSchedule.cardDialogFullWindow', {
                        start: cardDialogTimeDisplay.primaryStart,
                        end: cardDialogTimeDisplay.jobEnd,
                      })}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    sx={{ mt: 1 }}
                    label={
                      cardDialogEv.type === 'unassigned'
                        ? t('cleaningSchedule.unassigned')
                        : cardDialogEv.type === 'projected'
                          ? t('cleaningSchedule.cardDialogTypeProjected')
                          : cardDialogEv.recurrence_label || t('cleaningSchedule.cardDialogTypeScheduled')
                    }
                  />
                  {formatEventTotalHours(cardDialogEv) != null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} fontWeight={600}>
                      {t('cleaningSchedule.cardDialogTotalHours', { hours: formatEventTotalHours(cardDialogEv) })}
                    </Typography>
                  )}
                  {cardDialogEv.type === 'scheduled' &&
                    scheduledCleanerCount(cardDialogEv) > 1 &&
                    formatPerPersonJobHours(cardDialogEv) != null &&
                    formatEventTotalHours(cardDialogEv) != null && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {t('cleaningSchedule.cardDialogHoursPerPerson', {
                          each: formatPerPersonJobHours(cardDialogEv),
                          total: formatEventTotalHours(cardDialogEv),
                          count: scheduledCleanerCount(cardDialogEv),
                        })}
                      </Typography>
                    )}
                </Box>

                <Divider />

                {cardDialogEv.type === 'scheduled' && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('cleaningSchedule.cardDialogCleanersSection')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('cleaningSchedule.shiftTeamHint')}
                    </Typography>
                    {shiftTrackRows(cardDialogEv).map((row) => (
                      <Box
                        key={row.trackId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Avatar sx={{ width: 40, height: 40, bgcolor: row.employee.color || '#64748b' }}>
                          {row.employee.name?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="body1" sx={{ flex: 1 }} fontWeight={600}>
                          {row.employee.name}
                        </Typography>
                        {canEdit && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('cleaningSchedule.shiftRemoveOne')}
                            onClick={() => removeTrackFromCardDialog(row.trackId)}
                            disabled={shiftDeletingId != null}
                          >
                            {shiftDeletingId === row.trackId ? (
                              <CircularProgress size={22} color="inherit" />
                            ) : (
                              <DeleteOutline />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {canEdit &&
                      cardDialogEv.cleaning_detail_id &&
                      getAssignableEmployeesForJobDay(
                        cardDialogEv.cleaning_detail_id,
                        cardDialogEv.service_date
                      ).length > 0 && (
                        <Button
                          variant="outlined"
                          startIcon={<PersonAdd />}
                          disabled={shiftDeletingId != null}
                          onClick={() => {
                            const e = cardDialogEv;
                            setCardDialogOpen(false);
                            setCardDialogEv(null);
                            openAddCleanerFromScheduledEvent(e);
                          }}
                        >
                          {t('cleaningSchedule.cardDialogAddCleaner')}
                        </Button>
                      )}
                    {canEdit &&
                      cardDialogEv.cleaning_detail_id &&
                      getAssignableEmployeesForJobDay(cardDialogEv.cleaning_detail_id, cardDialogEv.service_date).length === 0 && (
                        <Button
                          variant="outlined"
                          startIcon={<PersonAdd />}
                          disabled
                        >
                          {t('cleaningSchedule.cardDialogAddCleaner')}
                        </Button>
                      )}
                  </>
                )}

                {cardDialogEv.type === 'unassigned' && (
                  <Typography variant="body2" color="text.secondary">
                    {t('cleaningSchedule.cardDialogUnassignedHelp')}
                  </Typography>
                )}

                {cardDialogEv.type === 'projected' && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {t('cleaningSchedule.cardDialogProjectedHelp', {
                        name: cardDialogEv.employee?.name || '',
                      })}
                    </Typography>
                    {canEdit && (
                      <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                        <Button
                          variant="contained"
                          disabled={cardDialogBusy}
                          onClick={() => placeProjectedOnCalendar(cardDialogEv)}
                        >
                          {cardDialogBusy ? t('common.saving') : t('cleaningSchedule.cardDialogPlaceOnCalendar')}
                        </Button>
                        {(() => {
                          const linkedId = cardDialogEv.employee?.id;
                          const extraRows = getAssignableEmployeesForJobDay(
                            cardDialogEv.cleaning_detail_id,
                            cardDialogEv.service_date
                          ).filter((r) => (linkedId == null ? true : String(r.employee?.id) !== String(linkedId)));
                          if (!extraRows.length) return null;
                          return (
                            <>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('cleaningSchedule.cardDialogProjectedAddCleanerHint', {
                                  name: cardDialogEv.employee?.name || t('cleaningSchedule.cardDialogLinkedCleanerFallback'),
                                })}
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<PersonAdd />}
                                disabled={cardDialogBusy}
                                onClick={() => {
                                  const e = cardDialogEv;
                                  setCardDialogOpen(false);
                                  setCardDialogEv(null);
                                  openAddCleanerFromProjectedEvent(e);
                                }}
                              >
                                {t('cleaningSchedule.cardDialogAddCleaner')}
                              </Button>
                            </>
                          );
                        })()}
                      </Stack>
                    )}
                  </>
                )}

                {cardDialogEv.type === 'unassigned' && canEdit && (cardDialogEv.assignable_cleaners || []).length > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      const job = unassignedJobPayloadFromEv(cardDialogEv);
                      setCardDialogOpen(false);
                      setCardDialogEv(null);
                      openAssignDialog(job);
                    }}
                  >
                    {t('cleaningSchedule.cardDialogAssign')}
                  </Button>
                )}

                {canEditCleaningJob && cardDialogEv.cleaning_detail_id && (
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlined />}
                    onClick={() => {
                      navigate(`/cleaning-details/${cardDialogEv.cleaning_detail_id}/edit`);
                      setCardDialogOpen(false);
                      setCardDialogEv(null);
                    }}
                  >
                    {t('cleaningSchedule.cardDialogEditJob')}
                  </Button>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCardDialog} disabled={shiftDeletingId != null || cardDialogBusy}>
                {t('common.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <CleaningScheduleAvailabilityDialog
        open={availabilityDialogOpen}
        onClose={() => setAvailabilityDialogOpen(false)}
        employees={employees}
        weekStart={payload?.week_start || weekStartStr}
        weekEnd={payload?.week_end || weekEndStr}
        defaultServiceDate={selectedDate}
        canEdit={canEdit}
        onSaved={loadWeek}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity: severity || 'success' })}
      />

      <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={closeCreateMenu}>
        <MenuItem
          onClick={() => {
            closeCreateMenu();
            openOnDemandDialog({ newClient: false });
          }}
        >
          {t('cleaningSchedule.addOnDemandForClient')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeCreateMenu();
            // For "new client", we still keep you in this screen by taking you to the client page in a new tab is not possible here.
            // Instead, we start with blank client selection and let you pick an existing client for now.
            openOnDemandDialog({ newClient: true });
          }}
        >
          {t('cleaningSchedule.addNewClientOnDemand')}
        </MenuItem>
      </Menu>

      <Dialog open={onDemandOpen} onClose={() => setOnDemandOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cleaningSchedule.onDemandTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="od-client">{t('cleaningSchedule.client')}</InputLabel>
                  <Select
                    labelId="od-client"
                    label={t('cleaningSchedule.client')}
                    name="client_id"
                    value={onDemandForm.client_id}
                    onChange={handleOnDemandChange}
                  >
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="service_date"
                  label={t('cleaningSchedule.serviceDate')}
                  type="date"
                  value={onDemandForm.service_date}
                  onChange={handleOnDemandChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="start_time"
                  label={t('cleaningSchedule.startTime')}
                  type="time"
                  value={onDemandForm.start_time}
                  onChange={handleOnDemandChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  name="horas"
                  label={t('cleaningSchedule.hours')}
                  type="number"
                  inputProps={{ min: 0, step: '0.25' }}
                  value={onDemandForm.horas}
                  onChange={handleOnDemandChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  name="hourly_rate"
                  label={t('cleaningSchedule.hourlyRate')}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={onDemandForm.hourly_rate}
                  onChange={handleOnDemandChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="wages"
                  label={t('cleaningSchedule.wages')}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={onDemandForm.wages}
                  onChange={handleOnDemandChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label={t('cleaningSchedule.notes')}
                  value={onDemandForm.notes}
                  onChange={handleOnDemandChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">{t('cleaningSchedule.onDemandInfo')}</Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnDemandOpen(false)} disabled={savingOnDemand}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveOnDemand} disabled={savingOnDemand}>
            {savingOnDemand ? t('common.saving') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CleaningScheduleDashboard;
